import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Info,
  XCircle,
} from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import StatusBadge from "@/components/ui/StatusBadge";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import type { Mandate } from "@/types/mandate";
import { mandateService } from "@/services/apiServices";
import DemoBanner from "./components/DemoBanner";
import ExpiredCard from "./components/ExpiredCard";
import { buildUmrn, formatNow } from "./data";

type Outcome = "success" | "declined" | "failed";

export default function MockAuthStatusPage() {
  return (
    <ToastProvider>
      <StatusInner />
    </ToastProvider>
  );
}

function StatusInner() {
  const { refNo = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const outcome = (searchParams.get("outcome") ?? "success") as Outcome;
  const [mandate, setMandate] = useState<Mandate | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    let isMounted = true;
    if (!refNo) {
      setLoading(false);
      return;
    }
    mandateService
      .getById(refNo)
      .then((res) => {
        if (isMounted && res.success && res.data?.mandate) {
          const apiM = res.data.mandate;
          setMandate({
            id: apiM.id || `MND-${refNo}`,
            refNo: apiM.refNo || refNo,
            retailerId: apiM.retailerId || "",
            retailerName: apiM.retailerName || "",
            shopName: apiM.shopName || "",
            customerName: apiM.customerName || "",
            customerMobile: apiM.customerMobile || "",
            customerEmail: apiM.customerEmail || "",
            bankName: apiM.bankName || "",
            accountNumber: apiM.accountNumber || "",
            ifsc: apiM.ifscCode || apiM.ifsc || "",
            accountType: apiM.accountType || "Savings",
            authMode: apiM.authMode || "eNACH",
            amount: Number(apiM.amount || apiM.emiAmount || apiM.totalPayable || 0),
            maxAmount: Number(apiM.maxAmount || apiM.totalPayable || apiM.productPrice || 0),
            frequency: apiM.frequency || "Monthly",
            startDate: apiM.startDate || "",
            endDate: apiM.endDate || "",
            purpose: apiM.purpose || "EMI Collection",
            status: apiM.status || (outcome === "success" ? "ACTIVE" : outcome === "declined" ? "REJECTED" : "FAILED"),
            umrn: apiM.umrn || null,
            authLink: `/auth/${refNo}`,
            failureReason: apiM.failureReason || null,
            createdAt: apiM.createdAt || new Date().toISOString(),
            updatedAt: apiM.updatedAt || new Date().toISOString(),
          });
        } else if (isMounted) {
          setMandate(null);
        }
      })
      .catch(() => {
        if (isMounted) setMandate(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [refNo, outcome]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-page px-6 py-6">
        <div className="flex items-center gap-3 rounded-xl border border-line bg-card p-6 shadow-sm text-ink font-medium">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
          <span>Verifying status...</span>
        </div>
      </div>
    );
  }

  if (!mandate) return <ExpiredCard />;

  function copyUmrn() {
    const umrnText = mandate?.umrn || buildUmrn(mandate);
    navigator.clipboard
      ?.writeText(umrnText)
      .then(() => toast.success("UMRN copied to clipboard"))
      .catch(() => toast.error("Could not copy UMRN"));
  }

  return (
    <div className="flex min-h-screen flex-col justify-center bg-page px-6 py-6">
      <div className="mx-auto flex w-full max-w-[480px] flex-col items-center text-center">
        <DemoBanner />

        {outcome === "declined" ? (
          <>
            <div className="mt-4 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-danger-bg">
              <XCircle className="h-10 w-10 text-danger" />
            </div>
            <h1 className="mt-4 text-xl font-semibold text-ink">Mandate declined</h1>
            <p className="mt-1 text-[13px] text-ink-secondary">
              The request was rejected during authorisation.
            </p>
            <div className="mt-5 w-full rounded-[12px] border border-line bg-card p-5 text-left shadow-card">
              <div className="divide-y divide-slate-100">
                <DetailRow label="Reference Number" value={<span className="font-medium">{refNo}</span>} />
                <DetailRow label="Status" value={<StatusBadge status="CANCELLED" />} />
                <DetailRow label="Reason" value={<span className="font-medium">Declined by customer</span>} />
                <DetailRow label="Attempted On" value={<span className="font-medium">{formatNow()}</span>} />
              </div>
            </div>
            <div className="mt-4 flex w-full items-start gap-2.5 rounded-lg border border-red-200 bg-danger-bg px-4 py-3 text-left">
              <p className="text-[13px] text-ink-secondary">
                Please contact the retailer who shared this link if this was a mistake.
              </p>
            </div>
          </>
        ) : outcome === "failed" ? (
          <>
            <div className="mt-4 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-warning-bg">
              <AlertTriangle className="h-10 w-10 text-warning" />
            </div>
            <h1 className="mt-4 text-xl font-semibold text-ink">Authorisation failed</h1>
            <p className="mt-1 text-[13px] text-ink-secondary">We could not complete the request.</p>
            <div className="mt-5 w-full rounded-[12px] border border-line bg-card p-5 text-left shadow-card">
              <div className="divide-y divide-slate-100">
                <DetailRow label="Reference Number" value={<span className="font-medium">{refNo}</span>} />
                <DetailRow label="Status" value={<StatusBadge status="HALTED" />} />
                <DetailRow label="Reason" value={<span className="font-medium">Bank server timeout</span>} />
                <DetailRow label="Attempted On" value={<span className="font-medium">{formatNow()}</span>} />
              </div>
            </div>
            <Button fullWidth className="mt-4 h-11" onClick={() => navigate(`/auth/${refNo}`)}>
              Try Again
            </Button>
          </>
        ) : (
          <>
            <div className="mt-4 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-success-bg">
              <CheckCircle2 className="h-10 w-10 text-success" />
            </div>
            <h1 className="mt-4 text-xl font-semibold text-ink">Mandate authorised successfully</h1>
            <p className="mt-1 text-[13px] text-ink-secondary">Your e-mandate is now active.</p>
            <div className="mt-5 w-full rounded-[12px] border border-line bg-card p-5 text-left shadow-card">
              <div className="divide-y divide-slate-100">
                <DetailRow label="Reference Number" value={<span className="font-medium">{refNo}</span>} />
                <DetailRow
                  label="UMRN"
                  value={
                    <span className="inline-flex items-center justify-end gap-1.5">
                      <span className="font-mono text-sm font-medium text-ink">
                        {mandate.umrn || buildUmrn(mandate)}
                      </span>
                      <button
                        type="button"
                        onClick={copyUmrn}
                        aria-label="Copy UMRN"
                        className="flex h-6 w-6 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  }
                />
                <DetailRow label="Status" value={<StatusBadge status="ACTIVE" />} />
                <DetailRow label="Authorised On" value={<span className="font-medium">{formatNow()}</span>} />
              </div>
            </div>
            <div className="mt-4 flex w-full items-start gap-2.5 rounded-lg border border-sky-200 bg-info-bg px-4 py-3 text-left">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" />
              <p className="text-[13px] text-ink-secondary">
                The first debit will be attempted on or after {mandate.startDate} as per the
                mandate schedule.
              </p>
            </div>
            <Button variant="secondary" fullWidth className="mt-4 h-11">
              Download Receipt
            </Button>
          </>
        )}

        <p className="mt-6 text-xs text-ink-muted">Secure eNACH mandate processing · MandatePro</p>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <span className="text-[13px] text-ink-secondary">{label}</span>
      <span className="text-right text-sm text-ink">{value}</span>
    </div>
  );
}